import React, { useEffect, useRef, useState } from 'react'

import { Field, Form, Formik } from 'formik'
import { pick } from 'lodash'
import { Col, Container, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import AsyncSelect from 'react-select/async'

import hands from '../assets/amani-thumb_up.png'
import { Gender, useAuth } from '../common/Auth'
import FormErrorMessage from '../common/FormErrorMessage'
import { createDate, loadOptionsForCompanySelector, toDateString } from '../common/helpers'
import { KeyIconSvg } from '../common/KeyIconSvg'
import NiceButton from '../common/NiceButton'
import PublicLayout from '../common/PublicLayout'
import { RequiredMark } from '../common/RequiredMark'
import { PriceTable } from '../home/PriceTable'
import axiosClient from '../service/axiosClient'
import { Package, PaymentPeriod } from '../types/gopay'


function RegisterPage() {
    const auth = useAuth()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const {t} = useTranslation()
    const [step, setStep] = useState<number>(1)

    const [searchParams,] = useSearchParams()
    const selectedUrlPackage = searchParams.get('package')
    const selectedUrlPeriod = searchParams.get('period')
    const [selectedPackage, setSelectedPackage] = useState<Package | null>(
        selectedUrlPackage && Object.values(Package).includes(selectedUrlPackage as Package)
            ? selectedUrlPackage as Package
            : null
    )
    const [selectedPeriod, setSelectedPeriod] = useState<PaymentPeriod | null>(
        selectedUrlPeriod && Object.values(PaymentPeriod).includes(selectedUrlPeriod as PaymentPeriod)
            ? selectedUrlPeriod as PaymentPeriod
            : null
    )
    console.log('selectedPackage', selectedPackage)
    // package
    const [errorMsg, setErrorMsg] = useState<string>('')
    const [successMsg, setSuccessMsg] = useState<boolean>(false)
    const [passwordShown, setPasswordShown] = useState(false)
    const [confirmPasswordShown, setConfirmPasswordShown] = useState(false)

    useEffect(() => {
        setSelectedPackage(
            selectedUrlPackage && Object.values(Package).includes(selectedUrlPackage as Package)
                ? selectedUrlPackage as Package
                : null
        )
        setSelectedPeriod(
            selectedUrlPeriod && Object.values(PaymentPeriod).includes(selectedUrlPeriod as PaymentPeriod)
                ? selectedUrlPeriod as PaymentPeriod
                : null
        )
    }, [selectedUrlPackage, selectedUrlPeriod])

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const defaultValues = {
        isCompany: false,
        businessName: '',
        billingStreet: '',
        billingCity: '',
        billingZipCode: '',
        icDph: '',
        dic: '',
        ico: '',
        email: '',
        password: '',
        confirmPassword: '',
        title: '',
        firstName: '',
        lastName: '',
        city: '',
        zipCode: '',
        country: '',
        street: '',
        web: '',
        phoneNumber: '',
        gender: undefined,
        getdph7aParagraph: false,
        vatPayer: false,
        acceptTerms: false,
        birthDate: undefined,
        affiliateId: '',
    }

    const maxDate = useRef(createDate(0, 0, -10))
    const minDate = useRef(createDate(0, 0, -100))

    const submit = async (values: typeof defaultValues) => {
        console.log('step', step)
        if (step === 1 || !selectedPackage || !selectedPeriod) {
            return
        }
        setErrorMsg('')
        setSuccessMsg(false)
        setIsLoading(true)
        const affiliateUuid = localStorage.getItem('affiliate-uuid')
        if (affiliateUuid) {
            values.affiliateId = affiliateUuid
            localStorage.removeItem('affiliate-uuid')
        }

        auth.register({
            ...pick(values, ['email', 'password', 'firstName', 'lastName', 'title', 'gender']),
            ...(values.isCompany ? {
                isCompany: values.isCompany,
                businessName: values.businessName,
                getdph7aParagraph: values.getdph7aParagraph,
                vatPayer: values.vatPayer,
                billingStreet: values.billingStreet,
                billingCity: values.billingCity,
                billingZipCode: values.billingZipCode,
                ico: values.ico,
                icDph: values.icDph,
                dic: values.dic,
            } : {
                address: {
                    street: values.street,
                    city: values.city,
                    zipCode: values.zipCode,
                    country: values.country,
                }
            }),
            contact: {
                countryPrefix: '+421',
                phoneNumber: values.phoneNumber,
                email: values.email,
                web: values.web,
            },
            // if affiliateUuid is set, it will be used, otherwise if it is empty string, it will be ignored
            affiliateId: affiliateUuid || undefined,
            birthDate: values.birthDate ? toDateString(values.birthDate) : undefined,
            selectedPackage: selectedPackage
        }).then((response) => {
            console.log('reg response', response)
            const userId = response.data.id

            if (selectedPackage !== Package.FREE) {
                axiosClient.post('/gopay/initialization', {
                    'paymentPeriod': selectedPeriod,
                    'selectedPackage': selectedPackage,
                    'userAccountId': userId,
                }).then((initGoPayResponse) => {
                    const redirectUrl = initGoPayResponse.data.gwUrl
                    window.location.href = redirectUrl
                })
            }
            setSuccessMsg(true)
        }).catch((error) => {
            console.log('error?.response?.data', error?.response?.data)
            const status = error?.response?.status
            const data = error?.response?.data
            if (status === 409) {
                setErrorMsg(t('email_already_registered'))
            } else if (status === 400 && typeof data === 'string') {
                // backend versions that answer 400 with the reason in the body
                setErrorMsg(data)
            } else {
                setErrorMsg(t('registration_failed'))
            }
            // formikHelpers.setFieldError('email', error?.response?.data || error?.response?.data?.message || 'Something went wrong')
            // window.scrollTo(0, 0)
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const validate = (values: any) => {
        const errors: any = {}
        console.log('validujeme')
        if (step === 1) {
            if (!values.email.trim()) {
                errors.email = t('required_field_error')
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
                errors.email = 'Nesprávny formát e-mailovej adresy'
            }

            if (!values.password.trim()) {
                errors.password = t('required_field_error')
            } else if (values.password.length < 4) {
                errors.password = 'Heslo musí mať viac ako 4 znaky'
            }

            if (!values.confirmPassword.trim()) {
                errors.confirmPassword = t('required_field_error')
            } else if (values.confirmPassword !== values.password) {
                errors.confirmPassword = 'Heslá sa musia zhodovať'
            }

        } else {
            if (!values.firstName.trim()) {
                errors.firstName = t('required_field_error')
            }

            if (!values.lastName.trim()) {
                errors.lastName = t('required_field_error')
            }
            console.log('sme tuu vo validacii')
            if (values.isCompany) {
                if (!values.businessName.trim()) {
                    errors.businessName = t('required_field_error')
                }
                if (!(new String(values.ico)).trim()) {
                    errors.ico = t('required_field_error')
                }
                if (!(new String(values.dic)).trim()) {
                    errors.dic = t('required_field_error')
                }
                if (!values.billingStreet.trim()) {
                    errors.billingstreet = t('required_field_error')
                }
                if (!values.billingCity.trim()) {
                    errors.billingCity = t('required_field_error')
                }
                if (!values.billingZipCode.trim()) {
                    errors.billingZipCode = t('required_field_error')
                }

                if (values.vatPayer || values.getdph7aParagraph) {
                    if (!(new String(values.icDph)).trim()) {
                        errors.icDph = t('required_field_error')
                    }
                }

            } else {
                if (!values.street.trim()) {
                    errors.street = t('required_field_error')
                }
                if (!values.city.trim()) {
                    errors.city = t('required_field_error')
                }
                if (!values.zipCode.trim()) {
                    errors.zipCode = t('required_field_error')
                }
            }

            if (!values.acceptTerms) {
                errors.acceptTerms = 'Musíte súhlasiť s podmienkami'
            }
        }
        console.log('errors', errors)
        return errors
    }

    return (
        <PublicLayout>
            {!selectedPackage || !selectedPeriod ? <Container><Row>
                    <div className="col-md-12"><PriceTable/></div>
                </Row></Container> :
                (successMsg ?
                    <Container>
                        <div className="row d-flex justify-content-center py-5">
                            <div className="col-md-5">
                                <h2 className="text-center mb-5">Už len jeden malý krôčik…</h2>
                                <div className="form-text text-center">
                                    Práve som ti poslala odkaz na aktiváciu tvojho Amani účtu na zadaný
                                    e-mail.<br/>
                                    Prosím o trpezlivosť, mal by ti pristáť v tvojej schránke do 5 minút.<br/>
                                    Nie je potrebné pozerať na hodinky, ale ak sa zdá, že sa pošta zatúlala,
                                    skontroluj
                                    aj
                                    priečinok SPAM (občas sa e-maily chcú hrať na schovávačku).
                                    <br/><br/><img src={hands} width={200} alt="congrats"/>
                                </div>
                            </div>
                        </div>
                    </Container> :
                    <Container>
                        <div className="row d-flex justify-content-center py-5">
                            <div className="col-md-5">
                                <h2 className="text-center mb-5">Registrácia</h2>
                                <div className="rounded-content-body">
                                    <Formik
                                        initialValues={defaultValues}
                                        validate={validate}
                                        onSubmit={submit}
                                    >{({
                                           values,
                                           validateForm,
                                           setFieldValue,
                                           setFieldTouched,
                                           errors,
                                           isSubmitting
                                       }) => {
                                        return (<>
                                            <Form>
                                                {/* <PrettyPrintJson data={values} /> */}
                                                {step === 1 ? <>
                                                    <div className="form-group">
                                                        <label htmlFor="email">Zadaj svoju e-mailovú adresu<RequiredMark/></label>
                                                        <Field id="email" name="email" className="form-control"/>
                                                        <FormErrorMessage name="email"/>
                                                    </div>

                                                    <div className="form-group">
                                                        <label htmlFor="password">Zvoľ heslo<RequiredMark/></label>
                                                        <div className="input-group show-psw-input-group">
                                                            <Field id="password"
                                                                   type={passwordShown ? 'text' : 'password'}
                                                                   name="password" className="form-control"/>
                                                            <div className="input-group-append">
                                                                <button
                                                                    onClick={(e: any) => setPasswordShown(!passwordShown)}
                                                                    className="" type="button"
                                                                    title={passwordShown ? 'Skryť heslo' : 'Zobraziť heslo'}>
                                                                    <KeyIconSvg/>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <FormErrorMessage name="password"/>
                                                    </div>
                                                    <div className="form-group">
                                                        <label htmlFor="confirmPassword">Zadaj heslo znova<RequiredMark/></label>
                                                        <div className="input-group show-psw-input-group">
                                                            <Field id="confirmPassword"
                                                                   type={confirmPasswordShown ? 'text' : 'password'}
                                                                   name="confirmPassword" className="form-control"/>
                                                            <div className="input-group-append">
                                                                <button
                                                                    onClick={(e: any) => setConfirmPasswordShown(!confirmPasswordShown)}
                                                                    className="" type="button"
                                                                    title={confirmPasswordShown ? 'Skryť heslo' : 'Zobraziť heslo'}>
                                                                    <KeyIconSvg/>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <FormErrorMessage name="confirmPassword"/>
                                                    </div>

                                                </> : <>

                                                    <h4 className={'text-left mb-3'}>Meno</h4>

                                                    <div className="form-group">
                                                        <label htmlFor="firstName">Meno<RequiredMark/></label>
                                                        <Field id="firstName" name="firstName"
                                                               className="form-control"/>
                                                        <FormErrorMessage name="firstName"/>
                                                    </div>
                                                    <div className="form-group">
                                                        <label htmlFor="lastName">Priezvisko<RequiredMark/></label>
                                                        <Field id="lastName" name="lastName" className="form-control"/>
                                                        <FormErrorMessage name="lastName"/>
                                                    </div>

                                                    <div className="form-group">
                                                        <label>Pohlavie</label>
                                                        <div className="mb-3">
                                                            <div className="form-check form-check-inline">
                                                                <Field type="radio" id="gender-male"
                                                                       className="form-check-input"
                                                                       name="gender"
                                                                       value={Gender.MALE}/>
                                                                <label className="form-check-label"
                                                                       htmlFor="gender-male">
                                                                    Muž
                                                                </label>
                                                            </div>
                                                            <div className="form-check form-check-inline">
                                                                <Field type="radio" id="gender-female"
                                                                       className="form-check-input"
                                                                       name="gender" value={Gender.FEMALE}/>
                                                                <label className="form-check-label"
                                                                       htmlFor="gender-female">
                                                                    Žena
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <FormErrorMessage name="gender"/>
                                                    </div>

                                                    <div className="form-group">
                                                        <label htmlFor="birthDate">Dátum narodenia</label>
                                                        <DatePicker
                                                            showYearDropdown
                                                            scrollableYearDropdown
                                                            yearDropdownItemNumber={100}
                                                            maxDate={maxDate.current}
                                                            minDate={minDate.current}
                                                            dateFormat="dd.MM.yyyy"
                                                            locale="sk"
                                                            //@ts-ignore
                                                            selected={(values.birthDate && new Date(values.birthDate)) || null}
                                                            onChange={(value: any) => {
                                                                setFieldValue('birthDate', value)
                                                            }} id="birthDate" name="birthDate" className="form-control"/>
                                                        <FormErrorMessage name="birthDate"/>
                                                    </div>

                                                    <div className="form-group form-check">
                                                        <Field type="checkbox" id="isCompany"
                                                               className="form-check-input"
                                                               name="isCompany"/>
                                                        <label className="form-check-label" htmlFor={'isCompany'}>
                                                            Chcem sa registrovať ako firma
                                                        </label>
                                                    </div>

                                                    {values.isCompany && <>
                                                        <div className="form-group">
                                                            <label htmlFor="businessName">Názov firmy<RequiredMark/></label>
                                                            <AsyncSelect
                                                                placeholder={'Začnite písať názov firmy...'}
                                                                className={'async-select'}
                                                                classNamePrefix={'async-select'}
                                                                loadingMessage={(inputValue) => <>Vyhľadávam...</>}
                                                                noOptionsMessage={(inputValue) => <>{inputValue.inputValue.length ? 'Nenašli sa žiadne výsledky' : 'Začnite písať...'}</>}
                                                                onChange={(item) => {
                                                                    console.log('sel', item)

                                                                    setFieldValue('businessName', item?.label)

                                                                    setFieldValue('ico', item?.value?.cin)
                                                                    setFieldValue('dic', item?.value?.tin)
                                                                    setFieldValue('icDph', item?.value?.vatin || '')
                                                                    setFieldValue('billingStreet', item?.value?.formatted_street)
                                                                    setFieldValue('billingCity', item?.value?.municipality)
                                                                    setFieldValue('billingZipCode', item?.value?.postal_code)

                                                                    setFieldValue('vatPayer', false)
                                                                    setFieldValue('getdph7aParagraph', false)
                                                                    if (item?.value?.vatin_paragraph === '§4') {
                                                                        setFieldValue('vatPayer', true)
                                                                        setFieldValue('getdph7aParagraph', false)
                                                                    }
                                                                    if (item?.value?.vatin_paragraph === '§7' || item?.value?.vatin_paragraph === '§7a') {
                                                                        setFieldValue('getdph7aParagraph', true)
                                                                        setFieldValue('vatPayer', false)
                                                                    }

                                                                    // setFieldValue('providerDph7aParagraph', item?.value?.vatin_paragraph === '§7a')
                                                                }}
                                                                cacheOptions
                                                                loadOptions={loadOptionsForCompanySelector}/>

                                                            <Field id="businessName" name="businessName"
                                                                   className="form-contro d-none"/>
                                                            <FormErrorMessage name="businessName"/>
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="businessName">IČO<RequiredMark/></label>
                                                            <Field id="ico" name="ico" className="form-control"/>
                                                            <FormErrorMessage name="ico"/>
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="businessName">DIČ<RequiredMark/></label>
                                                            <Field id="dic" name="dic" className="form-control"/>
                                                            <FormErrorMessage name="dic"/>
                                                        </div>

                                                        <div className="form-group form-check">
                                                            <input
                                                                name="vatPayer"
                                                                onChange={(e) => {
                                                                    setFieldValue('vatPayer', false)
                                                                    setFieldValue('getdph7aParagraph', false)
                                                                }}
                                                                checked={!values.vatPayer && !values.getdph7aParagraph}
                                                                type="checkbox"
                                                                id="vatPayerFalse"
                                                                className="form-check-input"/>
                                                            <label className="form-check-label" htmlFor="vatPayerFalse">
                                                                Subjekt nie je platiteľ DPH
                                                            </label>
                                                        </div>
                                                        <div className="form-group form-check">
                                                            <input
                                                                name="vatPayer"
                                                                onChange={(e) => {
                                                                    setFieldValue('vatPayer', true)
                                                                    setFieldValue('getdph7aParagraph', false)
                                                                }}
                                                                checked={values.vatPayer && !values.getdph7aParagraph}
                                                                type="checkbox"
                                                                id="vatPayerTrue"
                                                                className="form-check-input"/>
                                                            <label className="form-check-label" htmlFor="vatPayerTrue">
                                                                Subjekt je platiteľ DPH
                                                            </label>
                                                        </div>
                                                        <div className="form-group form-check">
                                                            <input
                                                                name="vatPayer"
                                                                onChange={(e) => {
                                                                    setFieldValue('vatPayer', false)
                                                                    setFieldValue('getdph7aParagraph', true)
                                                                }}
                                                                checked={values.getdph7aParagraph}
                                                                type="checkbox"
                                                                id="getdph7aParagraph"
                                                                className="form-check-input"/>
                                                            <label className="form-check-label"
                                                                   htmlFor={'getdph7aParagraph'}>
                                                                Subjekt je registrovaný podľa §7/§7a
                                                            </label>
                                                        </div>

                                                        {values.vatPayer || values.getdph7aParagraph ?
                                                            <div className="form-group">
                                                                <label htmlFor="icDph">IČ DPH<RequiredMark/></label>
                                                                <Field id="icDph" name="icDph"
                                                                       className="form-control"/>
                                                                <FormErrorMessage name="icDph"/>
                                                            </div> : ''}
                                                    </>}

                                                    <h4 className={'text-left mb-3 mt-4'}>Adresa</h4>
                                                    <div className="form-group">
                                                        <label htmlFor="title">Ulica<RequiredMark/></label>
                                                        <Field id="street"
                                                               name={values.isCompany ? 'billingStreet' : 'street'}
                                                               className="form-control"/>
                                                        <FormErrorMessage
                                                            name={values.isCompany ? 'billingStreet' : 'street'}/>
                                                    </div>
                                                    <div className="form-group">
                                                        <label htmlFor="zipCode">PSČ<RequiredMark/></label>
                                                        <Field id="zipCode"
                                                               name={values.isCompany ? 'billingZipCode' : 'zipCode'}
                                                               className="form-control"/>
                                                        <FormErrorMessage
                                                            name={values.isCompany ? 'billingZipCode' : 'zipCode'}/>
                                                    </div>
                                                    <div className="form-group">
                                                        <label htmlFor="city">Mesto<RequiredMark/></label>
                                                        <Field id="city"
                                                               name={values.isCompany ? 'billingCity' : 'city'}
                                                               className="form-control"/>
                                                        <FormErrorMessage
                                                            name={values.isCompany ? 'billingCity' : 'city'}/>
                                                    </div>
                                                    {!values.isCompany && <div className="form-group">
                                                        <label htmlFor="country">Krajina</label>
                                                        <Field id="country" name="country" className="form-control"/>
                                                        <FormErrorMessage name="country"/>
                                                    </div>}

                                                    <h4 className={'text-left mb-3 mt-4'}>Kontaktné údaje</h4>
                                                    <div className="form-group">
                                                        <label htmlFor="phoneNumber">Telefón</label>
                                                        <Field id="phoneNumber" name="phoneNumber"
                                                               className="form-control"/>
                                                        <FormErrorMessage name="phoneNumber"/>
                                                    </div>
                                                    <div className="form-group">
                                                        <label htmlFor="web">Web</label>
                                                        <Field id="web" name="web" className="form-control"/>
                                                        <FormErrorMessage name="web"/>
                                                    </div>

                                                    <div className="form-group form-check">
                                                        <Field type="checkbox" id="acceptTerms"
                                                               className="form-check-input"
                                                               name="acceptTerms"/>
                                                        <label className="form-check-label">
                                                            Týmto potvrdzujem, že som sa oboznámil/a s dokumentom
                                                            <a
                                                                href={'/dokumenty/vseobecne-podmienky.pdf'}
                                                                target="_blank" rel="noreferrer">„Všeobecné
                                                                podmienky“</a>, rozumiem jeho
                                                            obsahu a súhlasím s
                                                            podmienkami v ňom uvedenými. Zaväzujem sa dodržiavať
                                                            pravidlá a postupovať v súlade s právnymi predpismi vrátane
                                                            Nariadenia GDPR.
                                                        </label>

                                                        <FormErrorMessage name="acceptTerms"/>
                                                    </div>
                                                </>}


                                                <p className="text-center">* označené údaje sú povinné</p>

                                                {errorMsg &&
                                                    <div
                                                        className="text-danger form-text text-center mb-3">{errorMsg}</div>}

                                                {step === 1 &&
                                                    <NiceButton isLoading={isSubmitting} type="button"
                                                                onClick={async () => {
                                                                    const err = await validateForm()

                                                                    if (!Object.keys(err).length) {
                                                                        setStep(2)
                                                                    } else {

                                                                        Object.keys(err).map((item) => {
                                                                            setFieldTouched(item, true)
                                                                        })
                                                                    }
                                                                }}
                                                                className="btn btn-primary btn-amani">
                                                        Ďalej
                                                    </NiceButton>}
                                                {step === 2 && <>
                                                    <Row>
                                                        <Col>
                                                            <button type="button" className="btn btn-primary btn-amani"
                                                                    onClick={e => setStep(1)}>
                                                                Späť
                                                            </button>
                                                        </Col>
                                                        <Col>
                                                            <NiceButton isLoading={isLoading} type="submit"
                                                                        onClick={() => {
                                                                            setFieldTouched('acceptTerms', true)
                                                                        }} className="btn btn-primary btn-jobapp">
                                                                Registrovať
                                                            </NiceButton>
                                                        </Col>


                                                    </Row>
                                                </>}

                                            </Form>
                                        </>)
                                    }}

                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </Container>)
            }

        </PublicLayout>
    )
}

export default RegisterPage
